// Every train this app can draw, as paint rather than as code.
//
// `TrainScene.vue` used to have one train hard-coded into it: a High Capacity
// Metro Train in Metro Trains Melbourne's silver, navy and yellow, written
// straight into the template and the stylesheet. That was right while the app
// was Melbourne's. It stopped being right the moment the app grew four more
// cities, because the loader on a Sydney board was showing a Melbourne train,
// the loader on a Canberra board was showing a Melbourne train, and the one
// piece of the interface that is allowed to be a real thing was quietly
// telling everyone outside Victoria that this was somebody else's app.
//
// So the drawing moved here, as data. One entry per kind of train the app now
// serves, each of them a small set of numbers and colours the scene reads. The
// scene keeps its world — the crossing, the ramp, the sky-rail station, the
// four-second loop — and swaps only what runs through it.
//
// IMPORT-FREE, like `src/city.js` and `src/rules.js`, and for the same reason:
// the splash reads it on the first paint, before the store has answered
// anything, and nothing here may drag Vue or the network bundles in front of
// that.
//
// THE DESIGN VOCABULARY STILL HOLDS. The interface is achromatic; the trains
// are the one thing allowed a real colour; `--la-signal` red stays reserved
// for lateness and appears nowhere below. Every colour here is a livery a
// railway actually paints on a vehicle, taken from side-on reference of the
// real thing, and nothing here is a UI colour.
//
// ── HOW A LIVERY IS DRAWN ────────────────────────────────────────────────────
//
// The scene's units are the ones it has always used: the train occupies x 14
// to 182 in a 220-unit window, the rail is at y 40 and the body's floor at
// y 36. An entry gives:
//
//   shell      the body's outline — where it starts and ends, how high its
//              roof is, how round its nose and its corners are. The scene
//              turns this into the clip path everything else is drawn inside,
//              so a shorter, lower tram is two numbers rather than a branch.
//   body       the fill under everything: the bare metal or the base coat.
//   roof       a thin strip along the top, or null where the body colour runs
//              right over the roof.
//   side       the livery proper — the bands, sweeps and facets — as a list of
//              paths painted in order over the body. Paths rather than a mix
//              of rects and polygons so that the order is the list's order and
//              a dynamic element never has to guess its own namespace.
//   skirt      the strip below the doors, down to the floor.
//   nose       the front end: one path and one fill.
//   cabGlass   the driver's window, or null for a driverless train, which is
//              the whole visual difference between a metro and a train.
//   windows    the saloon windows, as rects.
//   doors      where the doors are, and what a door is painted like.
//   bogies     where the wheels are.
//   wheels     optional: round tyres, `{ x, y, r, tyre, rim, hub }`, drawn outside
//              the clip after the body, for a road vehicle whose wheels stand
//              below its floor. A bus has these and no bogies.
//   seam       the colour of the line between carriages, or null for a body
//              drawn as one continuous skin.
//   carriages  how many carriages the drawn set is. Three or four: the scene
//              is 220 units wide and its story is timed to a train 164 units
//              long, so a real seven-car set would either have to shrink or
//              run off both ends of the crossing.
//   pantograph whether there is one on the roof. The one fact on this list
//              that is about propulsion rather than paint, and it is here
//              because a VLocity is a diesel and drawing wires over it would
//              be wrong.
//   tram       whether this is a tram. Only used for the things that follow
//              from it — the articulation bellows — everything else a tram is
//              (lower, shorter) is already in `shell`.
//   name       what it is, for the caption under the splash and for anything
//              that needs to say the train's name out loud.
//
// ── WHY PATHS FOR THE LIVERY ─────────────────────────────────────────────────
//
// The two helpers below turn a rect or a polygon into the path that draws
// exactly the same shape, so the table can be written in whichever of the
// three is clearest for the marking in hand — a stripe is a rect, a swept
// wave is a polygon, a nose is a path — while the scene renders one kind of
// element in one loop in one order.

/** A rect, as the path that draws it. */
const box = (x, y, w, h) => `M${x} ${y}H${x + w}V${y + h}H${x}Z`;

/** A polygon's points, as the path that draws it. */
const poly = (points) => `M${points.trim().split(/\s+/).map((p) => p.replace(',', ' ')).join('L')}Z`;

/**
 * The outline every livery is drawn inside.
 *
 * Melbourne's numbers are the ones the scene has always used, and the string
 * this produces for them is character-for-character the path that was written
 * into the template by hand. That is not a coincidence and it is not allowed
 * to stop being true: the Melbourne train is the one people have been looking
 * at since the first build, and a refactor that moved it by half a unit would
 * be a refactor that changed the app while claiming not to.
 */
export function shellPath({ x0, x1, top, bottom, noseR, cornerR }) {
  return `M${x0 + cornerR} ${top} `
    + `h${x1 - noseR - x0 - cornerR} `
    + `a${noseR} ${noseR} 0 0 1 ${noseR} ${noseR} `
    + `v${bottom - cornerR - top - noseR} `
    + `a${cornerR} ${cornerR} 0 0 1 ${-cornerR} ${cornerR} `
    + `h${-(x1 - cornerR - x0 - cornerR)} `
    + `a${cornerR} ${cornerR} 0 0 1 ${-cornerR} ${-cornerR} `
    + `v${-(bottom - cornerR - top - cornerR)} `
    + `a${cornerR} ${cornerR} 0 0 1 ${cornerR} ${-cornerR} z`;
}

/** A heavy-rail body: the full length of the window, roof at 10. */
const RAIL_SHELL = { x0: 14, x1: 182, top: 10, bottom: 36, noseR: 14, cornerR: 4 };

/**
 * A tram body: lower and shorter, because a tram is.
 *
 * Twelve units shorter at the tail, six at the nose, and four units lower in
 * the roof. Any less and the difference reads as a rendering wobble rather
 * than as a different vehicle; any more and the tram stops reaching the
 * crossing at the moment the scene's timing expects a train to be there.
 */
const TRAM_SHELL = { x0: 26, x1: 176, top: 14, bottom: 36, noseR: 16, cornerR: 3 };

/**
 * A route bus: a long low box, a little rounder over the front. Shorter than a
 * train, so it is drawn at a bus's own proportion (about 5 : 1 here) rather
 * than stretched to the rail's length.
 */
const BUS_SHELL = { x0: 40, x1: 180, top: 8, bottom: 36, noseR: 6, cornerR: 4 };

/**
 * A symmetric tram body: the same rounded end at both ends, because a tram has
 * no front. (`shellPath` rounds the right end by `noseR` and the left by
 * `cornerR`; equal numbers make the outline a mirror image of itself.)
 */
const D_SHELL = { x0: 26, x1: 176, top: 15, bottom: 36, noseR: 5, cornerR: 5 };

/** Mirror a polygon's points about the middle of a symmetric body (x 26-176). */
const mirrorPts = (pts) => pts.trim().split(/\s+/).map((p) => { const [x, y] = p.split(','); return `${202 - Number(x)},${y}`; }).join(' ');

/** Facets drawn at one end, as marks, then the same facets mirrored at the other. */
const bothEnds = (facets) => [
  ...facets.map((f) => ({ d: poly(f.pts), fill: f.fill })),
  ...facets.map((f) => ({ d: poly(mirrorPts(f.pts)), fill: f.fill })),
];

/**
 * A disc as a many-sided polygon, for a wheel arch cut into the body. A polygon
 * rather than an arc, because the native renderers draw only corner roundings
 * from an arc command and would flatten a circle. (The tyres themselves are
 * `wheels`, drawn as true circles by every renderer.)
 */
const disc = (cx, cy, r, n = 28) => poly(Array.from({ length: n }, (_, i) => {
  const a = (i / n) * Math.PI * 2;
  return `${(cx + Math.cos(a) * r).toFixed(2)},${(cy + Math.sin(a) * r).toFixed(2)}`;
}).join(' '));

/**
 * A route number as lit segments, for a bus's destination display. Seven-
 * segment digits built from rects, because a livery is paths and nothing else:
 * no font, so it is the same on the web, in the widget and on Android.
 */
const SEGMENTS = {
  0: 'abcdef', 1: 'bc', 2: 'abdeg', 3: 'abcdg', 4: 'bcfg', 5: 'acdfg', 6: 'acdefg', 7: 'abc', 8: 'abcdefg', 9: 'abcdfg',
};
const routeNumber = (text, x, y, w, h, t, gap) => String(text).split('').map((ch, i) => {
  const dx = x + i * (w + gap);
  const mid = y + (h - t) / 2;
  const on = SEGMENTS[ch] ?? '';
  const bars = {
    a: box(dx, y, w, t), g: box(dx, mid, w, t), d: box(dx, y + h - t, w, t),
    f: box(dx, y, t, h / 2 + t / 2), b: box(dx + w - t, y, t, h / 2 + t / 2),
    e: box(dx, y + h / 2 - t / 2, t, h / 2 + t / 2), c: box(dx + w - t, y + h / 2 - t / 2, t, h / 2 + t / 2),
  };
  return [...on].map((k) => bars[k]).join('');
}).join('');

/** The door on nearly every one of these: silver panel, split down the middle. */
const door = (panel, trim, glass = '#0e1a26', shape = {}) => ({ panel, trim, glass, ...shape });

/** Two bogies, under the ends, where the scene has always drawn them. */
const RAIL_BOGIES = [{ x: 30, w: 26 }, { x: 126, w: 26 }];
const TRAM_BOGIES = [{ x: 40, w: 22 }, { x: 96, w: 22 }, { x: 142, w: 22 }];

/**
 * The cab, head-on, for a hole that cannot hold a side view.
 *
 * The Dynamic Island is a 28 point circle. A six-car set on the rim is a
 * straight stick. This is the front of the same vehicle: body, nose
 * colour, windshield, headlights. The minute sits in the glass, which is
 * what a destination display is for. Colours come from the livery, never
 * from the UI.
 *
 *   body    the metal
 *   accent  the nose / door colour — yellow on an HCMT, teal on a Metropolis
 *   glass   the windshield, dark, where the figure lives
 *   roof    a cap, or null
 *   band    a stripe across the face, or null
 *   lights  2 or 3
 *   tram    lower and rounder
 *   blunt   no cab — Sydney Metro, the number sits on the panel
 *   image   the photoreal front cut from the city sheet, or null
 */
const face = ({
  body, accent, glass = '#0e1a26', roof = null, band = null,
  lights = 2, tram = false, blunt = false, image = null,
}) => ({ body, accent, glass, roof, band, lights, tram, blunt, image });

/**
 * Every livery, keyed by id.
 *
 * The order is the order they drive through the splash: Melbourne first,
 * because that is the app's own city and the train everybody who has ever
 * opened this app has seen, then the rest by city and by how much of that
 * city's network they carry.
 */
export const LIVERIES = {
  /**
   * MELBOURNE — the High Capacity Metro Train, and the one entry that is not
   * a design decision.
   *
   * Every number and every colour below was lifted out of `TrainScene.vue`
   * unchanged, including the ones that are slightly odd: the nose path's arc
   * that is not quite a quarter circle, the facets that overshoot the clip,
   * the body rect that starts two units outside the outline. They stay odd.
   * This entry's job is to render the train the app already had, exactly, so
   * that everything else here is an addition rather than a redesign.
   *
   * NO PANTOGRAPH, which is the single place this table describes the drawing
   * rather than the vehicle. The HCMT is of course electric. The scene has
   * never drawn its pantograph — it sits on a car outside the 164 units the
   * scene shows — and adding one now would change the one train that must not
   * change. The electric trains that are new here get theirs.
   */
  'melbourne-metro': {
    id: 'melbourne-metro',
    name: 'High Capacity Metro Train',
    shell: RAIL_SHELL,
    body: '#c9ced4',
    roof: null,
    side: [
      { d: poly('14,34 40,34 27,25'), fill: '#0d3468' },
      { d: poly('27,25 40,34 44,25'), fill: '#5fbdea' },
      { d: poly('58,34 98,34 78,24.5'), fill: '#0d3468' },
      { d: poly('98,34 118,34 118,26'), fill: '#5fbdea' },
      { d: poly('114,34 158,34 136,24.5'), fill: '#0d3468' },
      { d: poly('158,8 190,8 190,38 158,38 165,24'), fill: '#123f78' },
      { d: poly('160,38 178,38 169,27'), fill: '#5fbdea' },
      { d: poly('160,8 172,8 165.5,16'), fill: '#5fbdea' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#f5c518' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#f5c518' },
    windows: [
      { x: 20, y: 14, w: 18, h: 9 },
      { x: 60, y: 14, w: 34, h: 9 },
      { x: 116, y: 14, w: 40, h: 9 },
    ],
    doors: [44, 100],
    door: door('#b9c0c8', '#f5c518'),
    bogies: RAIL_BOGIES,
    seam: null,
    carriages: 3,
    pantograph: false,
    tram: false,
    // Island front is the X’Trapolis 2.0 from the city sheet. The rail
    // still draws this HCMT in profile — that is the side the scene has.
    face: face({
      body: '#c9ced4', accent: '#f5c518', roof: null, band: '#0d3468', lights: 3,
      image: 'face-melbourne-metro',
    }),
  },

  /**
   * V/LINE — a VLocity, and the only diesel in the table.
   *
   * White with the purple sweep, which is what the regional railcars have worn
   * since the 2010s rebrand. The nose is raked rather than wrapped: a VLocity's
   * cab is a long forward slope, and that shape is most of how you tell one
   * from a suburban train at a level crossing before you can read the front.
   *
   * It is the livery for every `regional: true` line in Melbourne's bundle —
   * Geelong, Ballarat, Bendigo, Traralgon and the rest — because those are
   * V/Line services that happen to run through Melbourne's suburban stations,
   * and drawing an HCMT for a Warrnambool train would be plainly wrong to
   * anybody standing on the platform.
   */
  vline: {
    id: 'vline',
    name: 'V/Line VLocity',
    shell: RAIL_SHELL,
    body: '#f2f3f5',
    roof: '#d5d8dd',
    side: [
      { d: box(10, 8, 180, 5), fill: '#6b2d7a' },
      { d: poly('10,29 62,33.5 122,27 190,31 190,38 10,38'), fill: '#6b2d7a' },
      { d: poly('10,27.5 62,32 122,25.5 190,29.5 190,31 122,27 62,33.5 10,29'), fill: '#c0288f' },
    ],
    skirt: '#3a424c',
    nose: { d: poly('150,38 182,8 190,8 190,38'), fill: '#6b2d7a' },
    cabGlass: { d: 'M163 14 h9 l8 10 v2 h-19 z', fill: '#0e1a26', stroke: '#f2f3f5' },
    windows: [
      { x: 20, y: 15, w: 20, h: 9 },
      { x: 60, y: 15, w: 34, h: 9 },
      { x: 116, y: 15, w: 30, h: 9 },
    ],
    doors: [44, 100],
    door: door('#eceff2', '#f5c518'),
    bogies: RAIL_BOGIES,
    seam: '#c3c7cd',
    carriages: 3,
    pantograph: false,
    tram: false,
    face: face({
      body: '#f2f3f5', accent: '#6b2d7a', roof: '#d5d8dd', band: '#c0288f', lights: 2,
      image: 'face-vline',
    }),
  },

  /**
   * MELBOURNE TRAM — a D-class (D1), the three-section low-floor Yarra Trams
   * Citadis in the PTV coat that runs most Melbourne routes.
   *
   * A TRAM IS BI-DIRECTIONAL, so unlike every train here it has no front and
   * no back: both ends are the same driver's cab, mirror images, and the
   * drawing is symmetric about the middle of the shell (`symmetric: true`).
   * Two long end sections carry the PTV green faceted pattern and a sloped
   * dark windscreen; a shorter white middle section carries the yellow door
   * pairs and, over its middle, the pantograph. Grey bellows join the three,
   * a light-green line runs along the roof, and a low yellow strip with a
   * white lamp sits under each cab. Drawn from our own idea of a D1 seen
   * side-on, as a handful of flat polygons that survive being 36 pt wide.
   *
   * `liveryFor` never reaches it: Melbourne's train bundle has no light rail
   * line. The tram follow card (`src/mode-follow.js`) asks for it through
   * `CITY_TRAM`, because that card's leg is `tram`, not `lightrail`.
   */
  'melbourne-tram': {
    id: 'melbourne-tram',
    name: 'Yarra Trams D-class',
    shell: D_SHELL,
    body: '#f4f6f4',
    roof: '#9bdc4f',
    side: [
      // The PTV facets, concentrated on the outer half of each end section and
      // fading to white toward the middle: left end then its mirror.
      ...bothEnds([
        { pts: '26,15 62,15 50,34 26,34', fill: '#3fa535' },
        { pts: '26,15 46,15 34,34 26,34', fill: '#1d7a30' },
        { pts: '46,15 72,15 58,28', fill: '#86cf4a' },
        { pts: '34,34 54,22 66,34', fill: '#2c9438' },
        { pts: '62,15 78,15 70,24', fill: '#c5e8a0' },
      ]),
      // The light-green roof line, over the facets, and the lamp's white end.
      { d: box(26, 15, 150, 1.8), fill: '#9bdc4f' },
      { d: box(26, 32, 3.5, 1.6) + box(172.5, 32, 3.5, 1.6), fill: '#ffffff' },
    ],
    skirt: '#3a424c',
    // The yellow headlight strip, both ends.
    nose: { d: box(29.5, 32, 8, 1.6) + box(164.5, 32, 8, 1.6), fill: '#ffd400' },
    // The tall sloped windscreen, both ends: its outer edge leans out at the foot.
    cabGlass: { d: `${poly('31,16.5 41,16.5 41,31 27.5,31')}${poly(mirrorPts('31,16.5 41,16.5 41,31 27.5,31'))}`, fill: '#10191f' },
    // A near-continuous dark band: three windows per end section, one between
    // the middle section's two door pairs.
    windows: [
      ...[56, 64, 72].map((x) => ({ x, y: 19, w: 7, h: 11 })),
      { x: 97.5, y: 19, w: 7, h: 11 },
      ...[56, 64, 72].map((x) => ({ x: 202 - x - 7, y: 19, w: 7, h: 11 })),
    ],
    // One door by each cab, two in the middle section: yellow-framed, mostly glass.
    doors: [43, 86, 106, 149],
    door: door('#ffd400', '#ffd400', '#10191f', { w: 10, pane: 3.7, paneH: 14.5 }),
    bogies: [{ x: 47, w: 12 }, { x: 95, w: 12 }, { x: 143, w: 12 }],
    seam: '#6c737a',
    // Where the two bellows are: the end sections are the long ones.
    joins: [82, 120],
    // The pantograph, centred over the middle section.
    panX: 101,
    symmetric: true,
    carriages: 3,
    pantograph: true,
    tram: true,
    face: face({
      body: '#f4f6f4', accent: '#ffd400', roof: '#9bdc4f', band: '#3fa535', lights: 3, tram: true,
      image: 'face-melbourne-tram',
    }),
  },

  /**
   * MELBOURNE BUS — a generic low-floor route bus: a white body over a deep
   * black glazing band, orange geometric facets on the rear third, a route
   * number on the destination display.
   *
   * Our own drawing, not any operator's: no logos, no maker's name, no
   * operator's marks. What makes it a Melbourne route bus at a glance is the
   * proportion (a long box, glass nearly half the body's height over a white
   * skirt) and the white, black and orange colour blocking.
   *
   * ONE FRONT, ON THE RIGHT, like every other vehicle here: the app's vehicles
   * travel left to right, so the driver's end, the front door, the windscreen
   * and the lamp are at x1 and the tail is at x0. The front door sits in the
   * front overhang, just ahead of the front axle; the centre door is ahead of
   * the rear axle.
   *
   * EVERYTHING BUT THE WHEELS IS A MARK. Doors and windows are painted as
   * `side` paths, not as `doors` and `windows`: the three renderers draw those
   * two slightly differently (a rounded pane here, a square one there, a door
   * that reaches the roof), and a bus's flush glazed leaves must look the same
   * on the web, the lock screen and Android. The wheels are `wheels`, circles
   * that stand below the body line.
   *
   * `road: true` puts the loader on a street rather than through a station.
   */
  'melbourne-bus': {
    id: 'melbourne-bus',
    name: 'Melbourne route bus',
    shell: BUS_SHELL,
    body: '#f5f6f6',
    roof: '#b4bac0',
    road: true,
    side: [
      // The glazing band: deep, about 46% of the body, over a white skirt.
      { d: box(40, 11, 140, 13), fill: '#10151a' },
      // The panes, with thin black pillars between: rear saloon, centre door
      // to the display, display to front door.
      ...[46, 55.2, 64.4, 73.6, 82.8, 92].map((x) => ({ d: box(x, 11.8, 8, 11.4), fill: '#202b35' })),
      ...[113.4, 122.6].map((x) => ({ d: box(x, 11.8, 8, 11.4), fill: '#202b35' })),
      { d: box(149.4, 11.8, 4.4, 11.4), fill: '#202b35' },
      // The destination display behind the front door: a route number in amber.
      { d: box(131, 12.2, 17, 6.6), fill: '#06080a' },
      { d: routeNumber('733', 133.2, 13.5, 3.8, 4, 0.9, 0.9), fill: '#ffb32a' },
      // The centre door and the front door: a thin light frame round two dark
      // glazed leaves, flush with the side, from the floor line to the top of
      // the band. Nothing stands proud of the body.
      ...[[100.5, 11], [155, 11]].flatMap(([x, w]) => [
        { d: box(x, 11, w, 23.4), fill: '#d3d8dc' },
        { d: box(x + 0.7, 11.7, 4.6, 22), fill: '#18222b' },
        { d: box(x + 5.7, 11.7, 4.6, 22), fill: '#18222b' },
      ]),
      // The display cap over the windscreen (the windscreen is `cabGlass`).
      { d: box(169, 8.6, 11, 2.4), fill: '#10151a' },
      { d: box(170.6, 9.3, 7.4, 0.9), fill: '#ffb32a' },
      // Orange geometric facets: the rear third and the lower body only.
      { d: poly('40,24 64,24 54,36 40,36'), fill: '#f26a1f' },
      { d: poly('64,24 90,24 72,36 54,36'), fill: '#c9501a' },
      { d: poly('40,24 52,24 40,31'), fill: '#ff8a3d' },
      { d: poly('64,24 76,24 68,31'), fill: '#ff8a3d' },
      { d: poly('90,24 90,25.6 78,36 72,36'), fill: '#f26a1f' },
      { d: poly('40,8 50,8 40,16'), fill: '#f26a1f' },
      // The engine hatch at the tail.
      { d: box(41.6, 14, 3.4, 8), fill: '#2a3139' },
      // Wheel arches, cut round each tyre.
      { d: disc(75, 34, 6.5), fill: '#14181c' },
      { d: disc(146, 34, 6.5), fill: '#14181c' },
    ],
    skirt: '#2a3036',
    // The front: a white bumper under the lamp.
    nose: { d: poly('166,28 180,28 180,34.2 166,34.2'), fill: '#e4e7ea' },
    // The windscreen: tall, the full height of the band, wrapping the corner.
    cabGlass: { d: poly('169,11 180,11 180,24 169,24'), fill: '#0b0f12' },
    windows: [],
    doors: [],
    door: door('#d3d8dc', '#d3d8dc', '#18222b'),
    bogies: [],
    // Round tyres, centred a little below the body line, with a pale hub.
    wheels: [
      { x: 75, y: 34, r: 5.1, tyre: '#1b1f24', rim: '#b9c0c8', hub: '#4a5057' },
      { x: 146, y: 34, r: 5.1, tyre: '#1b1f24', rim: '#b9c0c8', hub: '#4a5057' },
    ],
    seam: null,
    carriages: 1,
    pantograph: false,
    tram: false,
    face: face({
      body: '#f5f6f6', accent: '#e4e7ea', roof: '#b4bac0', band: '#10151a', lights: 2,
      image: 'face-melbourne-bus',
    }),
  },

  /**
   * SYDNEY TRAINS — a Waratah, and the one double-decker in the table.
   *
   * Two rows of windows with the blue-and-yellow band running between them is
   * the whole silhouette of a Sydney suburban train, and it is worth the
   * second row: a single-deck silver train with a blue stripe would read as a
   * Melbourne train that had been repainted. The doors are yellow, as they
   * are on the real thing, and the cab front is the yellow wrap.
   */
  'sydney-trains': {
    id: 'sydney-trains',
    name: 'Waratah A-set',
    shell: RAIL_SHELL,
    body: '#c9ced4',
    roof: '#aeb4bb',
    side: [
      { d: box(10, 20.6, 180, 5.4), fill: '#f5c518' },
      { d: box(10, 22, 180, 2.6), fill: '#0a4f9c' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#f5c518' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#f5c518' },
    windows: [
      { x: 20, y: 13, w: 18, h: 6.4 },
      { x: 60, y: 13, w: 34, h: 6.4 },
      { x: 116, y: 13, w: 34, h: 6.4 },
      { x: 20, y: 27, w: 18, h: 6 },
      { x: 60, y: 27, w: 34, h: 6 },
      { x: 116, y: 27, w: 34, h: 6 },
    ],
    doors: [44, 100],
    door: door('#f5c518', '#d9ae12'),
    bogies: RAIL_BOGIES,
    seam: '#8d949c',
    carriages: 3,
    pantograph: true,
    tram: false,
    face: face({
      body: '#c9ced4', accent: '#f5c518', roof: '#aeb4bb', band: '#0a4f9c', lights: 2,
      image: 'face-sydney-trains',
    }),
  },

  /**
   * SYDNEY METRO — an Alstom Metropolis, and the only thing here with no
   * driver's window at all.
   *
   * That is the point of the entry. The M1 is driverless, the front of the
   * train is a flat teal panel with nobody behind it, and a passenger can
   * stand at the very front and look down the tunnel. So `cabGlass` is null,
   * the nose is a flat face rather than a wrap, and the shell's nose radius
   * drops from fourteen to six — a Metropolis is blunt where a Waratah is
   * rounded.
   *
   * The teal is #168388, which is not a colour chosen here: it is the colour
   * the Sydney bundle already carries for the M1 line, so the loader and the
   * line's own stripe are the same teal.
   */
  'sydney-metro': {
    id: 'sydney-metro',
    name: 'Alstom Metropolis',
    shell: { x0: 14, x1: 182, top: 10, bottom: 36, noseR: 6, cornerR: 4 },
    body: '#dfe3e7',
    roof: '#c3c8cd',
    side: [
      { d: box(10, 12, 180, 10), fill: '#3a424c' },
      { d: box(10, 22.4, 180, 3), fill: '#168388' },
      { d: box(166, 8, 24, 30), fill: '#168388' },
    ],
    skirt: '#3a424c',
    nose: { d: box(168, 8, 22, 30), fill: '#168388' },
    cabGlass: null,
    windows: [
      { x: 20, y: 13, w: 20, h: 8 },
      { x: 60, y: 13, w: 34, h: 8 },
      { x: 116, y: 13, w: 44, h: 8 },
    ],
    doors: [44, 100],
    door: door('#cdd2d7', '#168388'),
    bogies: RAIL_BOGIES,
    seam: '#b4bac1',
    carriages: 4,
    pantograph: true,
    tram: false,
    face: face({
      body: '#dfe3e7', accent: '#168388', glass: '#0e1a26', roof: '#c3c8cd',
      lights: 2, blunt: true, image: 'face-sydney-metro',
    }),
  },

  /**
   * SYDNEY LIGHT RAIL — a CAF Urbos, for L1, L2, L3, L4 and Newcastle.
   *
   * Red over white with the dark glazing band, on the tram shell: lower,
   * shorter, three bogies instead of two because an Urbos is articulated and
   * the middle one is what says so at this size.
   */
  'sydney-lightrail': {
    id: 'sydney-lightrail',
    name: 'CAF Urbos tram',
    shell: TRAM_SHELL,
    body: '#f2f3f5',
    roof: '#d5d8dd',
    side: [
      { d: box(22, 12, 160, 5), fill: '#be1622' },
      { d: box(22, 17, 160, 8), fill: '#2b333c' },
      { d: box(22, 33, 160, 5), fill: '#be1622' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M164 14 a16 16 0 0 1 14 16 v8 h-7 v-8 a10 10 0 0 0 -8 -10 z', fill: '#be1622' },
    cabGlass: { d: 'M156 17 h6 a13 13 0 0 1 12 13 v1 h-18 z', fill: '#0e1a26', stroke: '#f2f3f5' },
    windows: [
      { x: 32, y: 18, w: 22, h: 6.4 },
      { x: 68, y: 18, w: 26, h: 6.4 },
      { x: 116, y: 18, w: 30, h: 6.4 },
    ],
    doors: [58, 104],
    door: door('#2b333c', '#be1622'),
    bogies: TRAM_BOGIES,
    seam: '#5c636b',
    carriages: 3,
    pantograph: true,
    tram: true,
    face: face({
      body: '#f2f3f5', accent: '#be1622', roof: '#d5d8dd', lights: 3, tram: true,
      image: 'face-sydney-lightrail',
    }),
  },

  /**
   * NSW TRAINLINK — the XPT, for the regional lines out of Sydney.
   *
   * A blue power car with the white-and-sky sweep along its flank and the
   * yellow band across the top of the cab, hauling fluted stainless carriages
   * with a blue band through the windows. It is the train on the North Coast,
   * Southern and Western lines; the Canberra and Griffith Xplorers are diesel
   * railcars in the same colours, and at this size a regional diesel in NSW
   * TrainLink blue is a regional diesel in NSW TrainLink blue. No wire.
   */
  'nsw-xpt': {
    id: 'nsw-xpt',
    name: 'NSW TrainLink XPT',
    shell: RAIL_SHELL,
    body: '#9aa3ab',
    roof: '#7d868f',
    side: [
      { d: box(10, 13, 180, 8.4), fill: '#1f4fae' },
      { d: box(10, 24.6, 180, 0.8), fill: '#7d868f' },
      { d: box(10, 28.6, 180, 0.8), fill: '#7d868f' },
      { d: box(132, 8, 58, 30), fill: '#1e4fb5' },
      { d: poly('132,30 160,24 190,20 190,26 160,30 132,35'), fill: '#f4f5f6' },
      { d: poly('132,27 160,21.6 190,17.6 190,20 160,24 132,30'), fill: '#7fc4ea' },
    ],
    skirt: '#2a3038',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#1e4fb5' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#f2c200' },
    windows: [
      { x: 20, y: 14, w: 20, h: 6.4 },
      { x: 60, y: 14, w: 34, h: 6.4 },
      { x: 104, y: 14, w: 22, h: 6.4 },
    ],
    doors: [44, 98],
    door: door('#9aa3ab', '#7d868f'),
    bogies: RAIL_BOGIES,
    seam: '#6f7880',
    carriages: 3,
    pantograph: false,
    tram: false,
    // Drawn, not cut from the photo sheet: see `scripts/draw-livery-faces.py`.
    face: face({
      body: '#9aa3ab', accent: '#1e4fb5', roof: '#7d868f', band: '#f2c200', lights: 2,
      image: 'face-nsw-xpt',
    }),
  },

  /**
   * BRISBANE — New Generation Rollingstock, silver with Queensland Rail's
   * teal through the windows and the yellow front.
   *
   * The small magenta flick behind the cab is on the real thing and is the
   * one marking that stops the NGR reading as a teal Waratah.
   */
  brisbane: {
    id: 'brisbane',
    name: 'Queensland Rail NGR',
    shell: RAIL_SHELL,
    body: '#c9ced4',
    roof: '#aeb4bb',
    side: [
      { d: box(10, 12, 180, 10.5), fill: '#0d8388' },
      { d: box(10, 26, 180, 1.4), fill: '#b9c0c8' },
      { d: poly('154,12 172,12 163,22.5 145,22.5'), fill: '#8e3a80' },
      { d: poly('163,12 173,12 168,22.5 158,22.5'), fill: '#c0288f' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#ffc425' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#ffc425' },
    windows: [
      { x: 20, y: 13.4, w: 18, h: 7.6 },
      { x: 60, y: 13.4, w: 34, h: 7.6 },
      { x: 116, y: 13.4, w: 26, h: 7.6 },
    ],
    doors: [44, 100],
    door: door('#d7dade', '#b9c0c8'),
    bogies: RAIL_BOGIES,
    seam: '#8d949c',
    carriages: 3,
    pantograph: true,
    tram: false,
    face: face({
      body: '#c9ced4', accent: '#ffc425', roof: '#aeb4bb', band: '#0d8388', lights: 2,
      image: 'face-brisbane',
    }),
  },

  /**
   * THE GOLD COAST — a G:link Flexity, for the one light rail line in the
   * Brisbane bundle.
   *
   * Blue and yellow, and the only vehicle in this table whose body colour is
   * the dark one and whose markings are the light one.
   */
  'gcoast-lightrail': {
    id: 'gcoast-lightrail',
    name: 'G:link Flexity tram',
    shell: TRAM_SHELL,
    body: '#14479b',
    roof: '#0f3470',
    side: [
      { d: box(22, 28, 160, 6), fill: '#ffc425' },
      { d: box(22, 17, 160, 8), fill: '#1a2a44' },
      { d: poly('152,14 182,14 182,38 168,38'), fill: '#ffc425' },
    ],
    skirt: '#2c3a52',
    nose: { d: 'M164 14 a16 16 0 0 1 14 16 v8 h-8 v-8 a9 9 0 0 0 -7 -9 z', fill: '#ffc425' },
    cabGlass: { d: 'M156 17 h6 a13 13 0 0 1 12 13 v1 h-18 z', fill: '#0e1a26', stroke: '#ffc425' },
    windows: [
      { x: 32, y: 18, w: 22, h: 6.4 },
      { x: 68, y: 18, w: 26, h: 6.4 },
      { x: 116, y: 18, w: 26, h: 6.4 },
    ],
    doors: [58, 104],
    door: door('#1a2a44', '#ffc425'),
    bogies: TRAM_BOGIES,
    seam: '#5c636b',
    carriages: 3,
    pantograph: true,
    tram: true,
    face: face({
      body: '#14479b', accent: '#ffc425', roof: '#0f3470', lights: 3, tram: true,
      image: 'face-gcoast-lightrail',
    }),
  },

  /**
   * ADELAIDE — an A-City 4000 class, silver with the Adelaide Metro band.
   *
   * Yellow over red, low on the side under the windows, which is where
   * Adelaide puts it. The nose carries the same two colours in the same order
   * so the front and the side agree.
   */
  adelaide: {
    id: 'adelaide',
    name: 'A-City 4000 class',
    shell: RAIL_SHELL,
    body: '#c9ced4',
    roof: '#aeb4bb',
    side: [
      { d: box(10, 23, 180, 3.4), fill: '#f7b71b' },
      { d: box(10, 26.4, 180, 2.6), fill: '#c23b33' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#f7b71b' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#f7b71b' },
    windows: [
      { x: 20, y: 13, w: 18, h: 8.6 },
      { x: 60, y: 13, w: 34, h: 8.6 },
      { x: 116, y: 13, w: 34, h: 8.6 },
    ],
    doors: [44, 100],
    door: door('#d7dade', '#b9c0c8'),
    bogies: RAIL_BOGIES,
    seam: '#8d949c',
    carriages: 3,
    pantograph: true,
    tram: false,
    face: face({
      body: '#c9ced4', accent: '#f7b71b', roof: '#aeb4bb', band: '#c23b33', lights: 3,
      image: 'face-adelaide',
    }),
  },

  /**
   * THE GLENELG TRAM — an Alstom Citadis in Adelaide Metro red and yellow,
   * for the one light rail line in the Adelaide bundle.
   */
  'adelaide-tram': {
    id: 'adelaide-tram',
    name: 'Glenelg Citadis tram',
    shell: TRAM_SHELL,
    body: '#c23b33',
    roof: '#a23129',
    side: [
      { d: box(22, 17, 160, 8), fill: '#2b333c' },
      { d: box(22, 30, 160, 4), fill: '#f7b71b' },
      { d: poly('150,14 182,14 182,38 158,38'), fill: '#f7b71b' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M164 14 a16 16 0 0 1 14 16 v8 h-8 v-8 a9 9 0 0 0 -7 -9 z', fill: '#f7b71b' },
    cabGlass: { d: 'M156 17 h6 a13 13 0 0 1 12 13 v1 h-18 z', fill: '#0e1a26', stroke: '#f7b71b' },
    windows: [
      { x: 32, y: 18, w: 22, h: 6.4 },
      { x: 68, y: 18, w: 26, h: 6.4 },
      { x: 116, y: 18, w: 26, h: 6.4 },
    ],
    doors: [58, 104],
    door: door('#2b333c', '#f7b71b'),
    bogies: TRAM_BOGIES,
    seam: '#5c636b',
    carriages: 3,
    pantograph: true,
    tram: true,
    face: face({
      body: '#c23b33', accent: '#f7b71b', roof: '#a23129', lights: 3, tram: true,
      image: 'face-adelaide-tram',
    }),
  },

  /**
   * CANBERRA — a CAF Urbos in Canberra Metro red.
   *
   * The same vehicle as Sydney's light rail wearing a different coat, which is
   * itself true: the Urbos runs in both cities. Canberra's is white with a
   * broad red wave along the side rather than Sydney's flat red band, and that
   * wave is the only reason the two are not the same picture.
   *
   * This is the default for Canberra as well as its tram entry, because
   * Transport Canberra runs one line and it is light rail: there is no
   * heavier train to fall back to.
   */
  canberra: {
    id: 'canberra',
    name: 'Canberra Metro Urbos',
    shell: TRAM_SHELL,
    body: '#f2f3f5',
    roof: '#d5d8dd',
    side: [
      { d: poly('22,14 60,14 60,26 96,32 140,26 182,26 182,14 22,14'), fill: '#bd0021' },
      { d: poly('22,30 60,30 96,36 140,30 182,30 182,38 22,38'), fill: '#bd0021' },
      { d: box(22, 17, 160, 8), fill: '#2b333c' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M164 14 a16 16 0 0 1 14 16 v8 h-7 v-8 a10 10 0 0 0 -8 -10 z', fill: '#bd0021' },
    cabGlass: { d: 'M156 17 h6 a13 13 0 0 1 12 13 v1 h-18 z', fill: '#0e1a26', stroke: '#f2f3f5' },
    windows: [
      { x: 32, y: 18, w: 22, h: 6.4 },
      { x: 68, y: 18, w: 26, h: 6.4 },
      { x: 116, y: 18, w: 26, h: 6.4 },
    ],
    doors: [58, 104],
    door: door('#2b333c', '#bd0021'),
    bogies: TRAM_BOGIES,
    seam: '#5c636b',
    carriages: 3,
    pantograph: true,
    tram: true,
    face: face({
      body: '#f2f3f5', accent: '#bd0021', roof: '#d5d8dd', lights: 3, tram: true,
      image: 'face-canberra',
    }),
  },

  /**
   * PERTH — a C-series, the X'Trapolis that has been replacing the A-series
   * on the suburban lines since 2024.
   *
   * White, with the dark glazing band, the green stripe below it, and doors
   * picked out in yellow — the three things that make a Perth train a Perth
   * train from the platform, side-on, before you can see the front. The cab
   * is a white wrap round a black windscreen surround. Electric, so the wire.
   *
   * Named for the class rather than the network: the timetable licence does
   * not allow the operator's brand to be used alongside the data, and the
   * class is what the train is anyway.
   */
  perth: {
    id: 'perth',
    name: 'C-series',
    shell: RAIL_SHELL,
    body: '#eceef0',
    roof: '#c9ced4',
    side: [
      { d: box(10, 12.4, 180, 9.6), fill: '#2b333c' },
      { d: box(10, 24, 180, 3.2), fill: '#1e9a48' },
    ],
    skirt: '#3a424c',
    nose: { d: 'M172 10 a14 14 0 0 1 12 14 v14 h-6 v-13 a10 10 0 0 0 -8 -10 z', fill: '#f4f5f6' },
    cabGlass: { d: 'M161 13 h7 a11 11 0 0 1 11 11 v2 h-18 z', fill: '#0e1a26', stroke: '#15181c' },
    windows: [
      { x: 20, y: 13.4, w: 18, h: 7.6 },
      { x: 60, y: 13.4, w: 34, h: 7.6 },
      { x: 116, y: 13.4, w: 30, h: 7.6 },
    ],
    doors: [44, 100],
    door: door('#f2c500', '#c9a200'),
    bogies: RAIL_BOGIES,
    seam: '#b4bac1',
    carriages: 3,
    pantograph: true,
    tram: false,
    // No photograph-cut front exists for this one: the Island face is drawn
    // from these same colours by `scripts/draw-livery-faces.py`.
    face: face({
      body: '#eceef0', accent: '#f4f5f6', glass: '#15181c', roof: '#c9ced4', band: '#1e9a48', lights: 2,
      image: 'face-perth',
    }),
  },

  /**
   * PERTH'S REGIONAL RAILCARS — the WD-class diesels on the Kalgoorlie,
   * Merredin and Northam services.
   *
   * Fluted stainless steel, a blue window band, and the yellow cab with the
   * white sweep down its edge. A diesel, so no wire, exactly as the VLocity —
   * and the one other regional vehicle in the table, because a Prospector
   * drawn as a suburban C-series would be as wrong as a Warrnambool train
   * drawn as an HCMT. The Australind's new railcars are not drawn separately;
   * from the side, at this size, a regional diesel is a regional diesel.
   */
  'perth-regional': {
    id: 'perth-regional',
    name: 'WD-class railcar',
    shell: RAIL_SHELL,
    body: '#c3c8ce',
    roof: '#a9afb6',
    side: [
      { d: box(10, 13, 180, 9), fill: '#2d6fb7' },
      { d: box(10, 26, 180, 0.8), fill: '#a9afb6' },
      { d: box(10, 29, 180, 0.8), fill: '#a9afb6' },
      { d: poly('120,27 132,23 150,22 140,25'), fill: '#e8762d' },
      { d: poly('150,38 182,8 190,8 190,38'), fill: '#f6c200' },
      { d: poly('152,38 178,12 181,12 156,38'), fill: '#f4f5f6' },
    ],
    skirt: '#3a424c',
    nose: { d: poly('150,38 182,8 190,8 190,38'), fill: '#f6c200' },
    cabGlass: { d: 'M163 14 h9 l8 10 v2 h-19 z', fill: '#0e1a26', stroke: '#f6c200' },
    windows: [
      { x: 20, y: 14, w: 20, h: 7 },
      { x: 60, y: 14, w: 34, h: 7 },
      { x: 116, y: 14, w: 26, h: 7 },
    ],
    doors: [44, 100],
    door: door('#c3c8ce', '#8d949c'),
    bogies: RAIL_BOGIES,
    seam: '#8d949c',
    carriages: 3,
    pantograph: false,
    tram: false,
    face: face({
      body: '#c3c8ce', accent: '#f6c200', roof: '#a9afb6', band: '#2d6fb7', lights: 2,
      image: 'face-perth-regional',
    }),
  },
};

/**
 * The order the splash drives them through: Melbourne first, then the rest.
 *
 * Object key order in JavaScript is insertion order for string keys, so this
 * is the table's own order and there is no second list to keep in step with
 * the first.
 */
export const LIVERY_IDS = Object.keys(LIVERIES);

/** Melbourne, because that is the app that exists and the one people have. */
export const DEFAULT_LIVERY = 'melbourne-metro';

/**
 * What each city's trains look like when nothing more specific is known.
 *
 * Every id here is a key of the table above, and the test in
 * `tests/liveries.test.js` says so out loud: a typo in this map would not
 * throw, it would silently draw a Melbourne train in Brisbane, which is the
 * exact bug this file exists to fix.
 */
export const CITY_LIVERY = {
  melbourne: 'melbourne-metro',
  sydney: 'sydney-trains',
  brisbane: 'brisbane',
  adelaide: 'adelaide',
  canberra: 'canberra',
  perth: 'perth',
};

/**
 * What each city's regional trains look like.
 *
 * Melbourne's are VLocities and Perth's are the WD-class diesels. A city
 * absent here marks no line regional, and if one ever did it would get the
 * VLocity, which is at least a regional train.
 */
export const CITY_REGIONAL = {
  melbourne: 'vline',
  sydney: 'nsw-xpt',
  // The Sydney–Canberra trains Canberra borrows.
  canberra: 'nsw-xpt',
  perth: 'perth-regional',
};

/**
 * What each city's trams look like.
 *
 * Melbourne's is for the tram follow card: its train bundle carries no
 * `mode: 'lightrail'` line, so `liveryFor` never reaches it, but a followed
 * Yarra Trams ride (`src/mode-follow.js`) does. Canberra's tram is
 * Canberra's only vehicle, so its tram and its default are the same id.
 */
export const CITY_TRAM = {
  melbourne: 'melbourne-tram',
  sydney: 'sydney-lightrail',
  brisbane: 'gcoast-lightrail',
  adelaide: 'adelaide-tram',
  canberra: 'canberra',
};

/**
 * What each city's buses look like. Melbourne only: a city with no entry has
 * no bus artwork and its bus screens keep the badge-coloured glyph.
 */
export const CITY_BUS = { melbourne: 'melbourne-bus' };

/**
 * Which train to draw while a screen waits.
 *
 * Takes the city and, when the screen knows it, the line it is loading for —
 * a line record as `app-state.js` hands them out, with `mode`, `short` and
 * `regional` on it. Every rule below is a fact about the vehicle that turns
 * up, in the order that the more specific fact wins:
 *
 *   regional      a V/Line service in Melbourne, a regional railcar in Perth
 *                 — the two bundles that mark regional lines. Checked first,
 *                 because a regional line is a regional train whatever else is
 *                 true of it.
 *   lightrail     a tram, if that city has one. `CITY_TRAM` rather than one
 *                 shared tram because an Urbos in Sydney red and a Flexity in
 *                 Gold Coast blue are not the same vehicle.
 *   Sydney M1     the metro. Matched on `short` because that is what the
 *                 bundle carries and what the app shows on every chip; the
 *                 route id is a GTFS artefact that could change under us.
 *
 * Anything else — including a line that is not known at all — is the city's
 * own default. Never returns an id that is not in the table.
 */
export function liveryFor({ city, line } = {}) {
  const fallback = CITY_LIVERY[city] ?? DEFAULT_LIVERY;
  if (!line) return fallback;

  if (line.regional) return CITY_REGIONAL[city] ?? 'vline';
  if (line.mode === 'lightrail') return CITY_TRAM[city] ?? fallback;
  if (city === 'sydney' && line.short === 'M1') return 'sydney-metro';

  return fallback;
}

/**
 * WHICH VEHICLE EACH MODE DRAWS, by mode id then city. A mode with no entry has
 * no artwork yet (a bus outside Melbourne, say); callers fall back to the mode's
 * badge-coloured glyph. Adding a mode's vehicle is one line here and a face in
 * assets/livery-faces. Read by the follow card, the loaders and TrainGlyph.
 *
 * MELBOURNE'S TRAM is the livery `melbourne-tram` (the Yarra Trams D-class):
 * `liveryForMode(mode)` returns it, `livery('melbourne-tram')` is its drawing
 * (shell, side, nose, windows, doors, pantograph) for any surface that draws
 * a tram side-on. MELBOURNE'S BUS is `melbourne-bus`, drawn the same way.
 */
export const LIVERY_OF_MODE = { tram: CITY_TRAM, bus: CITY_BUS };

/** The livery id a mode's vehicle is drawn in, or null when it has none. */
export function liveryForMode(mode) {
  return LIVERY_OF_MODE[mode?.id]?.[mode?.city] ?? null;
}

/**
 * What a loading screen should roll: an explicit livery id wins (a train
 * screen knows its line), then the screen's mode's vehicle, then, for a mode
 * with no artwork, a simple glyph in the mode's badge colour. With neither a
 * livery nor a mode it is Melbourne's train, as the loader has always been.
 */
export function loaderVehicle({ mode = null, livery: id = '' } = {}) {
  if (id) return { kind: 'livery', id };
  if (!mode) return { kind: 'livery', id: DEFAULT_LIVERY };
  const own = liveryForMode(mode);
  if (own) return { kind: 'livery', id: own };
  return { kind: 'glyph', colour: mode.badge?.colour ?? null };
}

/**
 * The loader's caption. A loader on a mode screen speaks that mode's noun:
 * "Reading the train" becomes "Reading the tram" there, and a label with no
 * train in it is left alone. Without a mode (the train screens) the label is
 * exactly what the caller wrote.
 */
export function loaderCaption(label, mode = null) {
  const noun = mode?.labels?.one;
  if (!noun || !label) return label;
  return label.replace(/\btrain(s?)\b/gi, (m) => {
    const word = m.endsWith('s') ? (mode.labels.many ?? `${noun}s`) : noun;
    return m[0] === m[0].toUpperCase() ? word[0].toUpperCase() + word.slice(1) : word;
  });
}

/** A livery by id, never undefined. */
export function livery(id) {
  return LIVERIES[id] ?? LIVERIES[DEFAULT_LIVERY];
}

/**
 * How long one pass through the scene takes, and where the splash starts in it.
 *
 * Both of these are the scene's own numbers — `--loop` is four seconds and the
 * splash has always opened at 0.18 of it — restated here because two other
 * files now have to agree with them: the splash, which changes the train at
 * the end of every pass, and `App.vue`, which must not take the splash away
 * in the middle of one.
 */
export const PASS_MS = 4000;
export const SPLASH_PHASE = 0.18;

/** How long is left of the first pass when the splash opens part-way into it. */
export const FIRST_PASS_MS = Math.round(PASS_MS * (1 - SPLASH_PHASE));
